import { useState } from "react";
import { useAuth } from "@clerk/react";
import { Check, Send } from "lucide-react";
import { toast, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, Label, Textarea } from "@ndd/ui";
import { authenticatedApi, ApiRequestError } from "@/lib/api";
import type {
  MeetingNoteData,
  MeetingNoteShareRecipientKey,
  MeetingNoteShareRecipientOption,
} from "./meeting-note-types";
import { cn } from "@/lib/utils";

interface MeetingNoteShareDialogProps {
  rdId: string;
  note: MeetingNoteData;
  recipientOptions: MeetingNoteShareRecipientOption[];
  onShared: () => void;
}

export function MeetingNoteShareDialog({
  rdId,
  note,
  recipientOptions,
  onShared,
}: MeetingNoteShareDialogProps) {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [recipientKeys, setRecipientKeys] = useState<
    MeetingNoteShareRecipientKey[]
  >(() => (recipientOptions[0] ? [recipientOptions[0].key] : []));
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleRecipient(key: MeetingNoteShareRecipientKey, checked: boolean) {
    setRecipientKeys((current) =>
      checked
        ? Array.from(new Set([...current, key]))
        : current.filter((recipientKey) => recipientKey !== key),
    );
  }

  async function handleSend() {
    if (isSending) return;
    if (recipientKeys.length === 0) {
      setError("Choose at least one recipient.");
      return;
    }
    setIsSending(true);
    setError(null);

    try {
      await authenticatedApi(
        getToken,
        `/api/rds/${rdId}/meeting-notes/${note.id}/share`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ recipientKeys, message }),
        },
      );
      toast.success(
        recipientKeys.length === 1
          ? "Meeting note shared"
          : `Meeting note shared with ${recipientKeys.length} recipients`,
      );
      setOpen(false);
      setMessage("");
      onShared();
    } catch (sendError) {
      if (sendError instanceof ApiRequestError) {
        const body = sendError.body as
          | { error?: string; auditRecorded?: boolean }
          | undefined;
        // A failed send may still have been recorded for audit; refresh the
        // note so the failed attempt appears in the share history.
        if (body?.auditRecorded) onShared();
        setError(body?.error ?? "Failed to share meeting note.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={recipientOptions.length === 0}
          className="rounded-[10px] border-sand-200 text-sand-600 hover:text-foreground"
        >
          <Send className="h-3.5 w-3.5" />
          Share
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle>Share meeting note</DialogTitle>
          <DialogDescription>
            Send this saved Meeting Note by internal email. The full note body and optional message will be included.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-sand-500">
              Recipients
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              {recipientOptions.map((option) => {
                const checked = recipientKeys.includes(option.key);
                return (
                  <label key={option.key} className="relative w-fit">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) =>
                        toggleRecipient(option.key, event.target.checked)
                      }
                      className="peer sr-only"
                    />
                    <span
                      className={cn(
                        "inline-flex h-8 items-center gap-2 rounded-full border border-sand-200 bg-card px-3 text-xs font-medium text-sand-600 shadow-sm transition-colors hover:bg-sand-100 peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2",
                        checked && "border-interactive bg-interactive-light text-bark",
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-3.5 w-3.5 items-center justify-center rounded-full border border-sand-300 bg-background text-transparent transition-colors",
                          checked && "border-interactive bg-interactive text-white",
                        )}
                      >
                        <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      </span>
                      {option.name}
                    </span>
                  </label>
                );
              })}
            </div>
            {recipientOptions.length === 0 && (
              <p className="text-sm text-sand-500">
                Meeting Note sharing recipients are not configured.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="meeting-note-share-message" className="text-[11px] font-semibold uppercase tracking-wider text-sand-500">
              Message <span className="normal-case text-sand-400">(optional)</span>
            </Label>
            <Textarea
              id="meeting-note-share-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              maxLength={2000}
              placeholder="Add context for the recipient..."
              className="min-h-[110px] rounded-xl border-sand-200 bg-sand-50 text-sm"
            />
          </div>

          {error && <p className="text-sm text-error">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isSending}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isSending || recipientKeys.length === 0}
            onClick={handleSend}
          >
            {isSending ? "Sending..." : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
